import { ShareRole } from "@zapier/bff";
import { Key, Select, SelectItem } from "@zapier/design-system";
import capitalize from "lodash.capitalize";
import { useCallback, useMemo } from "react";

type CallbackMap<T extends string> = Record<T, (action: T) => void>;

interface ShareAccessSelectProps<AdditionalActions extends string = string> {
  /** Additional actions with callbacks that can be performed on the share (e.g. "remove" could be an action) */
  additionalActions?: CallbackMap<AdditionalActions>;
  /** Optional labels for additional action keys (e.g. "no-access" -> "No access") */
  additionalActionLabels?: Record<string, string>;
  /** If the select is disabled */
  isDisabled?: boolean;
  /** Callback when the selected role changes */
  onSelectionChange?: (newRole: ShareRole) => void;
  /** The roles available for selection */
  roles: ShareRole[];
  /** The currently selected role or additional action key, if any */
  selectedRole?: ShareRole | AdditionalActions;
}

/** Returns a human-readable label for a share role */
function getRoleLabel(role: ShareRole) {
  return capitalize(role).replaceAll("_", " ");
}

export function ShareAccessSelect({
  additionalActions = {},
  additionalActionLabels,
  isDisabled,
  onSelectionChange,
  roles,
  selectedRole,
}: ShareAccessSelectProps) {
  const isShareRole = useMemo(() => {
    const rolesSet = new Set<unknown>(roles);
    return (value: unknown): value is ShareRole => {
      return rolesSet.has(value);
    };
  }, [roles]);

  const handleChange = useCallback(
    (value: Key | null) => {
      if (!value) {
        return;
      }

      if (value in additionalActions) {
        const action = additionalActions[value];
        if (action) {
          action(value as keyof typeof additionalActions);
        }
        return;
      }

      if (isShareRole(value) && value !== selectedRole) {
        onSelectionChange?.(value);
      }
    },
    [additionalActions, isShareRole, onSelectionChange, selectedRole],
  );

  return (
    <Select
      isLabelHidden={true}
      isDisabled={isDisabled}
      label="Role"
      onChange={handleChange}
      placeholder="Select role"
      selectedKey={selectedRole}
    >
      {roles.map((role) => (
        <SelectItem key={role} textValue={role} value={role}>
          {getRoleLabel(role)}
        </SelectItem>
      ))}
      {Object.keys(additionalActions).map((key) => (
        <SelectItem key={key} textValue={key} value={key}>
          {additionalActionLabels?.[key] ?? key}
        </SelectItem>
      ))}
    </Select>
  );
}
